/**
 * Module registry — maps each per-child / per-family module to its route slug,
 * label key and the scope that gates it. The shell uses this to render tabs and
 * to hide modules the caller can't access (using @solandia/core/permissions).
 *
 * On top of the scope gate, every module declares the family `modes` in which it
 * is offered. The shell layers a mode filter (`isModuleVisibleInMode`) over the
 * scope gate so co-parenting-only surfaces (agreements, audit) stay hidden while
 * the family runs in the default intact-family `'family'` mode.
 */
import {
  canAccess,
  canManageFamily,
  type FamilyMode,
  type Scope,
} from '@solandia/core';
import type { Dictionary } from '../i18n/sk';

export type ModuleScope = Scope | 'parent';

export interface ModuleDef {
  slug: string;
  /**
   * i18n key under `t.nav`, used when `label` is not provided. New family-mode
   * modules whose copy has not yet landed in the dictionary set `label` instead.
   */
  labelKey?: keyof Dictionary['nav'];
  /**
   * Literal label override. Takes precedence over `labelKey`. Used by modules
   * (e.g. Home/Deti/Nákupy) added ahead of their i18n keys; once the dictionary
   * carries the key this can be dropped in favour of `labelKey`.
   */
  label?: string;
  /**
   * Scope required to view. 'parent' = family-admin only. `null` = no scope gate
   * (visible to any active member), used by family-wide surfaces like Home.
   */
  scope: ModuleScope | null;
  /** Whether the module is per-child (needs an active child) or family-wide. */
  perChild: boolean;
  /** Family modes this module is offered in. Filtered on top of the scope gate. */
  modes: readonly FamilyMode[];
  /** Emoji glyph shown in the left sidebar / mobile nav (decorative). */
  icon: string;
}

/** Both modes — the default for everything that isn't separated-only. */
const ALL_MODES: readonly FamilyMode[] = ['family', 'separated'];
/** Separated-households mode only (co-parenting machinery). */
const SEPARATED_ONLY: readonly FamilyMode[] = ['separated'];

export const MODULES: ModuleDef[] = [
  // Home leads the sidebar: the warm family-at-a-glance landing surface.
  { slug: 'home', label: 'Domov', scope: null, perChild: false, modes: ALL_MODES, icon: '🏠' },
  { slug: 'children', label: 'Deti', scope: 'children', perChild: false, modes: ALL_MODES, icon: '👧' },
  { slug: 'contacts', labelKey: 'contacts', scope: 'contacts', perChild: true, modes: ALL_MODES, icon: '📇' },
  { slug: 'health', labelKey: 'health', scope: 'health', perChild: true, modes: ALL_MODES, icon: '🩺' },
  { slug: 'calendar', labelKey: 'calendar', scope: 'calendar', perChild: true, modes: ALL_MODES, icon: '📅' },
  { slug: 'shopping', label: 'Nákupy', scope: 'shopping', perChild: false, modes: ALL_MODES, icon: '🛒' },
  { slug: 'documents', labelKey: 'documents', scope: 'documents', perChild: true, modes: ALL_MODES, icon: '📄' },
  { slug: 'messages', labelKey: 'messages', scope: 'messages', perChild: false, modes: ALL_MODES, icon: '💬' },
  { slug: 'expenses', labelKey: 'expenses', scope: 'expenses', perChild: false, modes: ALL_MODES, icon: '💶' },
  { slug: 'agreements', labelKey: 'agreements', scope: 'parent', perChild: false, modes: SEPARATED_ONLY, icon: '🤝' },
  { slug: 'sharing', labelKey: 'sharing', scope: 'parent', perChild: false, modes: ALL_MODES, icon: '👨‍👩‍👧' },
  { slug: 'audit', labelKey: 'audit', scope: 'parent', perChild: false, modes: SEPARATED_ONLY, icon: '📜' },
];

/**
 * Shared visibility rule for a module given the caller's membership.
 * 'parent' modules require family-admin; a `null` scope is always visible to an
 * active member; others require the named scope.
 */
export function isModuleVisible(
  mod: ModuleDef,
  membership: Parameters<typeof canAccess>[0],
): boolean {
  if (mod.scope === null) return true;
  return mod.scope === 'parent'
    ? canManageFamily(membership)
    : canAccess(membership, mod.scope);
}

/** True when the module is offered in the given family mode. */
export function isModuleInMode(mod: ModuleDef, mode: FamilyMode): boolean {
  return mod.modes.includes(mode);
}

/**
 * Full visibility rule: the scope gate AND the family-mode gate. The shell uses
 * this everywhere it renders or routes modules so a member never sees (or can
 * route to) a surface that is hidden in the current mode.
 */
export function isModuleVisibleInMode(
  mod: ModuleDef,
  membership: Parameters<typeof canAccess>[0],
  mode: FamilyMode,
): boolean {
  return isModuleInMode(mod, mode) && isModuleVisible(mod, membership);
}
